/**
 * Where a lead came from: the first page of the visit, its campaign tags and
 * the site that referred it, recorded once per browser session
 * (LeadSourceRecorder) and sent with the contact form, so the inquiry email
 * names the source. Pure: callers pass the location and referrer.
 */
export const LEAD_SOURCE_KEY = 'kl-lead-source'
export const LEAD_SOURCE_MAX = 500

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']

export interface LeadSource {
  landing: string
  referrer?: string
  utm: Record<string, string>
}

/** The first touch from the page a visit starts on; an internal referrer isn't one. */
export function firstTouch(href: string, referrer: string): LeadSource {
  const url = new URL(href)
  const utm: Record<string, string> = {}
  for (const key of UTM_KEYS) {
    const value = url.searchParams.get(key)?.trim()
    if (value) utm[key] = value.slice(0, 100)
  }
  let from: string | undefined
  try {
    const ref = new URL(referrer)
    if (ref.host !== url.host) from = ref.host
  } catch {}
  return { landing: url.pathname, ...(from ? { referrer: from } : {}), utm }
}

/** One line for the inquiry: campaign tags, landing page, referrer, and the page the form was sent from. */
export function formatLeadSource(first: LeadSource | null, page: string): string {
  const parts = [
    ...Object.entries(first?.utm ?? {}).map(([key, value]) => `${key}=${value}`),
    first ? `landing=${first.landing}` : null,
    first?.referrer ? `referrer=${first.referrer}` : null,
    `page=${page}`,
  ].filter(Boolean)
  return parts.join('; ').slice(0, LEAD_SOURCE_MAX)
}

/** Reads a stored first touch, or null when missing or malformed. */
export function parseLeadSource(raw: string | null): LeadSource | null {
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as LeadSource
    return typeof value?.landing === 'string' && value.utm && typeof value.utm === 'object' ? value : null
  } catch {
    return null
  }
}
